'use client';

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { createClient } from '@/lib/supabase/client';

export interface Tenant {
  id: string;
  slug: string;
  brand_name: string;
  brand_tagline: string | null;
  status: 'active' | 'pending_setup' | 'suspended' | 'provisioning';
  logo_url: string | null;
  favicon_url: string | null;
  primary_color: string;
  secondary_color: string;
  subdomain: string;
  custom_domain: string | null;
  domain_verified: boolean;
  support_email: string | null;
  support_phone: string | null;
}

interface TenantContextType {
  tenant: Tenant | null;
  isLoading: boolean;
  error: string | null;
  isSuperadminPortal: boolean;
  refreshTenant: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export function TenantProvider({ 
  children, 
  initialTenant = null,
  isSuperadminPortal = false 
}: { 
  children: ReactNode;
  initialTenant?: Tenant | null;
  isSuperadminPortal?: boolean;
}) {
  const [tenant, setTenant] = useState<Tenant | null>(initialTenant);
  const [isLoading, setIsLoading] = useState(!initialTenant && !isSuperadminPortal);
  const [error, setError] = useState<string | null>(null);
  const supabase = createClient();

  const fetchTenant = async () => {
    if (isSuperadminPortal) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Get hostname from browser
      const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
      
      if (!hostname) {
        setIsLoading(false);
        return;
      }

      // Check for superadmin portal
      if (hostname === 'admin.belugahealth.com' || hostname === 'admin.belugahealth.local') {
        setIsLoading(false);
        return;
      }

      // Try to resolve tenant from hostname
      let tenantData: Tenant | null = null;

      // Check custom domain first
      const { data: customDomainTenant } = await supabase
        .from('tenants')
        .select('*')
        .eq('custom_domain', hostname)
        .eq('domain_verified', true)
        .eq('status', 'active')
        .single();

      if (customDomainTenant) {
        tenantData = customDomainTenant;
      } else if (hostname.endsWith('.belugahealth.com') || hostname.endsWith('.belugahealth.local')) {
        // Extract subdomain
        const subdomain = hostname.split('.')[0];
        
        // Skip reserved subdomains
        const reserved = ['admin', 'api', 'www', 'app', 'mail', 'staging'];
        if (!reserved.includes(subdomain)) {
          const { data: subdomainTenant } = await supabase
            .from('tenants')
            .select('*')
            .eq('subdomain', subdomain)
            .eq('status', 'active')
            .single();

          tenantData = subdomainTenant;
        }
      }

      if (tenantData) {
        setTenant(tenantData);
        
        // Apply tenant branding via CSS variables
        if (typeof document !== 'undefined') {
          document.documentElement.style.setProperty('--color-primary', tenantData.primary_color);
          document.documentElement.style.setProperty('--color-secondary', tenantData.secondary_color);
        }
      } else {
        setError('Site not found');
      }
    } catch (err: any) {
      console.error('Error fetching tenant:', err);
      setError(err.message || 'Failed to load site');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!initialTenant && !isSuperadminPortal) {
      fetchTenant();
    }
  }, []);

  const refreshTenant = async () => {
    await fetchTenant();
  };

  return (
    <TenantContext.Provider value={{ 
      tenant, 
      isLoading, 
      error, 
      isSuperadminPortal,
      refreshTenant 
    }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}

// Utility hook to require a tenant
export function useRequiredTenant() {
  const { tenant, isLoading, error } = useTenant();
  
  if (isLoading) {
    return { tenant: null, isLoading: true, error: null };
  }
  
  if (error || !tenant) {
    return { tenant: null, isLoading: false, error: error || 'Tenant required' };
  }
  
  return { tenant, isLoading: false, error: null };
}
